import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Image, File, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Item, ItemContent, ItemMedia, ItemTitle } from '@/components/ui/item';

import { formatFileSize } from '@/shared/lib';
import type { AttachmentSummary, LocalAttachment } from '@/entities/consignments';

import { getTypeLabelRecords } from '../model/options';

interface AttachmentItemProps {
  attachment: AttachmentSummary | LocalAttachment;
  onDownload: (attachment: AttachmentSummary | LocalAttachment) => void;
  onRemove?: (attachment: AttachmentSummary | LocalAttachment) => void;
  isLocal?: boolean;
}

const getFileIcon = (contentType?: string, fileName?: string) => {
  if (!contentType && fileName) {
    const ext = fileName.toLowerCase().split('.').pop();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext || '')) return Image;
    if (ext === 'pdf') return FileText;
    return File;
  }

  if (contentType?.startsWith('image/')) return Image;
  if (contentType === 'application/pdf') return FileText;
  return File;
};

const getFileSize = (attachment: AttachmentSummary | LocalAttachment): number => {
  if ('fileSize' in attachment) {
    return attachment.fileSize;
  }
  if ('file' in attachment && attachment.file) {
    return attachment.file.size;
  }
  return 0;
};

export function AttachmentItem({ attachment, onDownload, onRemove, isLocal }: AttachmentItemProps) {
  const { t } = useTranslation();

  const contentType = 'contentType' in attachment ? attachment.contentType : undefined;
  const Icon = useMemo(() => getFileIcon(contentType, attachment.fileName), [contentType, attachment.fileName]);

  const typeLabelRecords = useMemo(() => getTypeLabelRecords(t), [t]);
  const fileSize = getFileSize(attachment);

  return (
    <Item>
      <ItemContent>
        <Button
          type="button"
          variant="link"
          size="sm"
          className="w-max justify-start items-center p-0 text-left"
          onClick={() => onDownload(attachment)}
          aria-label={`Download ${attachment.fileName}`}>
          <ItemMedia className="mt-1">
            <Icon />
          </ItemMedia>

          <ItemTitle>
            {attachment.fileName}
            {isLocal && (
              <Badge variant="outline" className="ml-2 text-xs">
                Pending
              </Badge>
            )}
          </ItemTitle>
        </Button>

        <div className="ml-6 flex flex-col gap-2 text-sm text-muted-foreground" data-slot="item-description">
          <div className="flex items-center gap-4">
            <span>{formatFileSize(fileSize)}</span>
            <Badge variant="secondary" className="text-xs">
              {typeLabelRecords[attachment.attachmentType]}
            </Badge>
            {isLocal && onRemove && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onRemove(attachment)}
                className="h-6 w-6 p-0"
                aria-label={`Remove ${attachment.fileName}`}>
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          {attachment.comment && <span className="italic">• {attachment.comment}</span>}
        </div>
      </ItemContent>
    </Item>
  );
}
